import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Skeleton, List, ListItemAvatar, Avatar, ListItemText, IconButton, ListItemButton, Drawer, useMediaQuery, Fade, Slide, Card, CardContent, InputAdornment, Chip, Paper, Divider, Tooltip, Zoom, Menu, MenuItem, Tabs, Tab, Checkbox, FormControlLabel } from '@mui/material';
import { useTheme, alpha, styled } from '@mui/material/styles';
import {
  Search, ChevronLeft, Edit, PersonAdd, ChatBubbleOutline, ChevronRight, AddComment,
  ArrowBack, Close, Send, MoreVert, Refresh, Campaign as CampaignIcon, Handshake as PartnershipIcon,
  Star, StarBorder, Archive, Inbox, CheckCircle, RadioButtonUnchecked, CheckBox, CheckBoxOutlineBlank, Groups as GroupsIcon, FiberManualRecord as FiberManualRecordIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../../API';
import ChatScreen from '../../Components/ChatScreen';
import CampaignChatScreen from '../../Components/ChatComponents/CampaignChatScreen';
import { replaceConsoleError } from '../../Utils/toastNotifications';
import PartnershipChatScreen from '../../Components/ChatComponents/PartnershipChatScreen';
import useIsDesktop from '../../Hooks/useIsDesktop';
import useAuth from '../../Hooks/use-auth';
import { useCreatorAuth } from '../../Hooks/creator-use-auth';
import useAlertDialog from '../../Components/useAlertDialog';
import AlertDialog from '../../Components/AlertDialog';
import ParticipantsPanel from '../../Components/ChatComponents/chatextra';
import ConversationDetailsPanel from '../../Components/ChatComponents/conversationdetailspanel';
import UpdateConversationDetailsDialog from '../../Components/ChatComponents/updatedetails';
import ChatModalCreator from '../../Components/ChatComponents/creatorchatmodal';
import CreateChatScreen from '../../Components/ChatComponents/newchat';
import BubbleChatScreen from '../../Components/ChatComponents/BubbleChatScreen';
import BubbleInfoSidebar from '../../Components/ChatComponents/BubbleChatSidebar';
import { useSocket } from '../../Context/SocketContext';

/* ------------------------------------------------------------------ */
/* Styled Components & Constants */
/* ------------------------------------------------------------------ */
const BOTTOM_NAV_HEIGHT = 86;
const HEADER_HEIGHT = 64;
const CONVERSATION_SIDEBAR_DEFAULT_WIDTH = 400;
const CONVERSATION_SIDEBAR_MIN_WIDTH = 320;
const CONVERSATION_SIDEBAR_MAX_WIDTH = 560;
const Z_INDEX = {
  MOBILE_CHAT_OVERLAY: 1000,
  DIALOG_BACKDROP: 1300,
  DIALOG: 1400,
  MOBILE_DIALOG: 1500,
};

const StyledSearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 28,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    transition: theme.transitions.create(['background-color', 'box-shadow']),
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
}));

const ConversationCard = styled(Paper)(({ theme, selected }) => ({
  margin: theme.spacing(0, 0.5),
  borderRadius: 8,
  border: 'none',
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    duration: 150,
  }),
  cursor: 'pointer',
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, 0.15)
    : 'transparent',
  boxShadow: selected ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}` : 'none',
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.hover, 0.1),
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
    '& .more-menu-btn': {
      opacity: 1,
    },
  },
  '&:active': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  // background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  background:"#ECECEC",
  backdropFilter: 'blur(10px)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  flexShrink: 0,
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  height: '100%',
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 48,
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: 3,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 48,
  minWidth: 0,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 600,
  '& .MuiTab-iconWrapper': {
    marginRight: theme.spacing(0.5),
    marginBottom: '0 !important',
  },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

/* ------------------------------------------------------------------ */
/* Enhanced ConversationsHub Component with Bubble Support */
/* ------------------------------------------------------------------ */
export default function ConversationsHub() {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = !useIsDesktop();
  const location = useLocation();
  const navigate = useNavigate();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const { isConnected: socketConnected, roomPresence, getRoomOnlineUsers } = useSocket();
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isMobileSafari = isMobile && isSafari;

  /* ---------------- Auth & User Type Detection ---------------- */
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const { creatorToken } = useCreatorAuth();
  const userId = userInfo?.id || creatorToken?.creator_user?.username || null;
  const isCreator = !!creatorToken;
  const isUser = !!userInfo && !creatorToken;
  const hasParticipantAccess = isCreator || isUser;

  /* ---------------- Enhanced State Management ---------------- */
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const [addParticipantOpen, setAddParticipantOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [updateDetailsDialogOpen, setUpdateDetailsDialogOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [newChatUserId, setNewChatUserId] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [contextMenuAnchor, setContextMenuAnchor] = useState(null);
  const [contextMenuConvo, setContextMenuConvo] = useState(null);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedConversationIds, setSelectedConversationIds] = useState(new Set());
  const [isBubbleSidebarMinimized, setIsBubbleSidebarMinimized] = useState(false);
  const [conversationSidebarWidth, setConversationSidebarWidth] = useState(CONVERSATION_SIDEBAR_DEFAULT_WIDTH);

  /* ---------------- Dynamic API Configuration ---------------- */
  const apiConfig = useMemo(() => {
    if (isCreator) {
      return {
        queryKey: 'creatorConversationsOrganized',
        fetchFn: () => API.conversations.fetchOrganizedConversations(),
        createFn: (params) => API.conversations.checkCreatorToUser(params),
      };
    } else {
      return {
        queryKey: ['messages', 'userOrganizedConversations'],
        fetchFn: () => API.conversations.fetchOrganizedConversations(),
        createFn: (params) => API.conversations.checkUserToUser(params),
      };
    }
  }, [isCreator]);

  const getDialogZIndex = useCallback(() => {
    if (isMobile && selectedConversationId) {
      return Z_INDEX.MOBILE_DIALOG;
    }
    return Z_INDEX.DIALOG;
  }, [isMobile, selectedConversationId]);

  /* ---------------- Enhanced Queries ---------------- */
  const {
    data: organizedConversations = { active: [], starred: [], archived: [], requested: [] },
    isLoading: isLoadingConversations,
    error: convError,
    refetch: refetchConversations,
    isFetching: isFetchingConversations
  } = useQuery(
    apiConfig.queryKey,
    apiConfig.fetchFn,
    {
      initialData: { active: [], starred: [], archived: [], requested: [] },
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
      enabled: !!(isCreator || isUser),
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  const {
    data: selectedConversationData,
    isLoading: isLoadingSelectedConversation,
    error: selectedConversationError,
  } = useQuery(
    ['conversation', 'single', selectedConversationId],
    () => API.conversations.getMetadata(selectedConversationId),
    {
      enabled: !!selectedConversationId && selectedConversationId !== 'new',
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
    }
  );

  // const {
  //   data: usersList = [],
  //   isLoading: isLoadingUsers
  // } = useQuery(
  //   ['users'],
  //   () => API.users.publicList(),
  //   {
  //     refetchOnWindowFocus: false,
  //     initialData: [],
  //     staleTime: 1000 * 60 * 10,
  //   }
  // );

  const {
    data: conversationMetadata,
    isLoading: isLoadingMetadata
  } = useQuery(
    ["messages", "metadata", selectedConversationId],
    () => API.conversations.getMetadata(selectedConversationId),
    {
      enabled: !!selectedConversationId && selectedConversationId !== 'new',
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
    }
  );

  /* ---------------- Enhanced Mutations ---------------- */
  const createNewChatMutation = useMutation(
    (params) => apiConfig.createFn(params),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(apiConfig.queryKey);
        handleSelectConversation(res.conversation_id);
      },
      onError: (err) => {
        replaceConsoleError('Error creating new conversation', err);
        openDialog('Error', `Error creating conversation: ${err?.response?.data?.error || err.message}`);
      }
    }
  );

  const inviteByEmailMutation = useMutation(
    (emailsArray) => API.conversations.inviteByEmail(selectedConversationId, emailsArray),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(apiConfig.queryKey);
        queryClient.invalidateQueries(['messages', selectedConversationId]);
        handleCloseAddParticipant();
      },
      onError: (err) => {
        replaceConsoleError('Error inviting by email', err);
        openDialog('Error', `Error inviting to conversation: ${err?.response?.data?.error || err.message}`);
      }
    }
  );

  const updateConversationDetailsMutation = useMutation(
    (details) => API.conversations.updateConversationDetails(selectedConversationId, details),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(apiConfig.queryKey);
        if (selectedConversationId) {
          queryClient.invalidateQueries(['messages', selectedConversationId]);
        }
        setUpdateDetailsDialogOpen(false);
      },
      onError: (err) => {
        replaceConsoleError('Error updating conversation details', err);
      }
    }
  );

  const updateStatusMutation = useMutation(
    ({ conversationId, status }) => API.conversations.updateConversationStatus(conversationId, status),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(apiConfig.queryKey);
        handleCloseContextMenu();
        console.log(`Conversation status updated to: ${variables.status}`);
      },
      onError: (err) => {
        replaceConsoleError('Error updating conversation status', err);
        openDialog('Error', `Failed to update conversation status: ${err?.response?.data?.error || err.message}`);
      }
    }
  );

  const acceptRequestMutation = useMutation(
    (conversationId) => API.conversations.acceptConversationRequest(conversationId),
    {
      onSuccess: (data, conversationId) => {
        queryClient.invalidateQueries(apiConfig.queryKey);
        console.log(`Conversation request accepted: ${conversationId}`);
      },
      onError: (err) => {
        replaceConsoleError('Error accepting conversation request', err);
        openDialog('Error', `Failed to accept conversation: ${err?.response?.data?.error || err.message}`);
      }
    }
  );

  const bulkUpdateStatusMutation = useMutation(
    ({ conversationIds, status }) => API.conversations.bulkUpdateConversationStatus(conversationIds, status),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(apiConfig.queryKey);
        setIsMultiSelectMode(false);
        setSelectedConversationIds(new Set());
        console.log(`Bulk updated ${variables.conversationIds.length} conversations to ${variables.status}`);
      },
      onError: (err) => {
        replaceConsoleError('Error bulk updating conversation status', err);
        openDialog('Error', `Failed to bulk update conversations: ${err?.response?.data?.error || err.message}`);
      }
    }
  );

  /* ---------------- Helper Functions ---------------- */
  const getConvoName = useCallback((convo) => {
    if (convo.display_name) return convo.display_name;
    if (convo.bubble_name) return convo.bubble_name;
    if (convo.campaign_name) return convo.campaign_name;
    if (convo.partnership_name) return convo.partnership_name;
  
    const otherUser = convo.participants?.find((p) => p.id !== userId);
  
    if (otherUser) {
      if (otherUser.first_name || otherUser.last_name) {
        const firstName = otherUser.first_name || '';
        const lastName = otherUser.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        if (fullName) return fullName;
      }
    
      if (otherUser.name) return otherUser.name;
      if (otherUser.full_name) return otherUser.full_name;
      if (otherUser.username) return otherUser.username;
      if (otherUser.email) return otherUser.email.split('@')[0];
    }
  
    return `Conversation #${convo.id}`;
  }, [userId]);

  const getConvoAvatar = useCallback((convo) => {
    if (convo.is_bubble_chat && convo.cover_image) {
      return convo.cover_image;
    }
    if (convo.cover_image && typeof convo.cover_image === 'string' && convo.cover_image.trim()) {
      return convo.cover_image;
    }
  
    const otherUser = convo.participants?.find((p) => p.id !== userId);
  
    if (otherUser) {
      const possibleAvatarProps = [
        'phphref',
        'profile_photo',
        'profile_picture',
        'avatar',
        'photo',
        'image',
        'picture'
      ];
    
      for (const prop of possibleAvatarProps) {
        if (otherUser[prop] && typeof otherUser[prop] === 'string' && otherUser[prop].trim()) {
          return otherUser[prop];
        }
      }
    }
  
    return '';
  }, [userId]);

  const getLastMessagePreview = useCallback((convo) => {
    if (convo.message) {
      return convo.message.length > 50
        ? `${convo.message.substring(0, 50)}...`
        : convo.message;
    }
    return 'No messages yet';
  }, []);

  const isBubbleConversation = useCallback((convo) => {
    return !!(convo.is_bubble_chat || convo.bubble_id || convo.bubble_name);
  }, []);

  const isCampaignConversation = useCallback((convo) => {
    return !!(
      convo.campaign_name ||
      convo.campaign_id ||
      (convo.display_name && convo.display_name.toLowerCase().includes('campaign'))
    );
  }, []);

  const isPartnershipConversation = useCallback((convo) => {
    return !!(
      convo.partnership_name ||
      convo.partnership_id ||
      (convo.display_name && convo.display_name.toLowerCase().includes('partnership'))
    );
  }, []);

  const currentTabConversations = useMemo(() => {
    let conversations = [];
  
    if (activeTab === 'active') {
      conversations = [
        ...(organizedConversations.starred || []),
        ...(organizedConversations.active || [])
      ];
    } else if (activeTab === 'archived') {
      conversations = organizedConversations.archived || [];
    } else if (activeTab === 'requested') {
      conversations = organizedConversations.requested || [];
    }
  
    return conversations;
  }, [activeTab, organizedConversations]);

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return currentTabConversations;
    const lowerQuery = searchQuery.toLowerCase();
    return currentTabConversations.filter((convo) =>
      getConvoName(convo).toLowerCase().includes(lowerQuery) ||
      getLastMessagePreview(convo).toLowerCase().includes(lowerQuery)
    );
  }, [currentTabConversations, searchQuery, getConvoName, getLastMessagePreview]);

  const selectedConversation = useMemo(() => {
    const allConversations = [
      ...(organizedConversations.starred || []),
      ...(organizedConversations.active || []),
      ...(organizedConversations.archived || []),
      ...(organizedConversations.requested || [])
    ];
  
    const fromList = allConversations.find((c) => c.id === selectedConversationId);
    if (fromList) {
      return fromList;
    }
  
    if (selectedConversationData?.conversation) {
      return selectedConversationData.conversation;
    }
  
    return null;
  }, [organizedConversations, selectedConversationId, selectedConversationData]);

  const getSelectedConversationChatType = useCallback(() => {
    if (!selectedConversation) return 'regular';
  
    if (isBubbleConversation(selectedConversation)) {
      return 'bubble';
    }

    const isPartnership = !!(
      selectedConversation.partnership_name ||
      selectedConversation.partnership_id ||
      conversationMetadata?.conversation?.partnership_id ||
      (selectedConversation.display_name && selectedConversation.display_name.toLowerCase().includes('partnership'))
    );
  
    if (isPartnership) return 'partnership';
  
    const isCampaign = !!(
      selectedConversation.campaign_name ||
      selectedConversation.campaign_id ||
      conversationMetadata?.conversation?.campaign_id ||
      (selectedConversation.display_name && selectedConversation.display_name.toLowerCase().includes('campaign'))
    );
  
    if (isCampaign) return 'campaign';
  
    return 'regular';
  }, [selectedConversation, conversationMetadata, isBubbleConversation]);

  const getCampaignId = useCallback(() => {
    return selectedConversation?.campaign_id || conversationMetadata?.conversation?.campaign_id || null;
  }, [selectedConversation, conversationMetadata]);

  const getPartnershipId = useCallback(() => {
    return selectedConversation?.partnership_id || conversationMetadata?.conversation?.partnership_id || null;
  }, [selectedConversation, conversationMetadata]);

  const getBubbleId = useCallback(() => {
    return selectedConversation?.bubble_id || conversationMetadata?.conversation?.bubble_id || null;
  }, [selectedConversation, conversationMetadata]);

  const getCreatorId = useCallback(() => {
    return conversationMetadata?.conversation?.creator_id || null;
  }, [conversationMetadata]);

  const getChatScreenType = useCallback((convo) => {
    if (convo.id === selectedConversationId) {
      return getSelectedConversationChatType();
    }
  
    if (isBubbleConversation(convo)) {
      return 'bubble';
    } else if (isPartnershipConversation(convo)) {
      return 'partnership';
    } else if (isCampaignConversation(convo)) {
      return 'campaign';
    }
    return 'regular';
  }, [selectedConversationId, getSelectedConversationChatType, isBubbleConversation, isPartnershipConversation, isCampaignConversation]);

  /* ---------------- Context Menu Handlers ---------------- */
  const handleOpenContextMenu = useCallback((event, convo) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuAnchor(event.currentTarget);
    setContextMenuConvo(convo);
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuAnchor(null);
    setContextMenuConvo(null);
  }, []);

  const handleStatusChange = useCallback((conversationId, newStatus) => {
    updateStatusMutation.mutate({ conversationId, status: newStatus });
  }, [updateStatusMutation]);

  const handleAcceptRequest = useCallback((conversationId) => {
    acceptRequestMutation.mutate(conversationId);
  }, [acceptRequestMutation]);

  /* ---------------- Multi-Select Handlers ---------------- */
  const toggleMultiSelectMode = useCallback(() => {
    setIsMultiSelectMode(prev => !prev);
    setSelectedConversationIds(new Set());
  }, []);

  const toggleConversationSelection = useCallback((conversationId) => {
    setSelectedConversationIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(conversationId)) {
        newSet.delete(conversationId);
      } else {
        newSet.add(conversationId);
      }
      return newSet;
    });
  }, []);

  const selectAllConversations = useCallback(() => {
    const allIds = new Set(filteredConversations.map(c => c.id));
    setSelectedConversationIds(allIds);
  }, [filteredConversations]);

  const deselectAllConversations = useCallback(() => {
    setSelectedConversationIds(new Set());
  }, []);

  const handleBulkAction = useCallback((status) => {
    if (selectedConversationIds.size === 0) return;
    bulkUpdateStatusMutation.mutate({
      conversationIds: Array.from(selectedConversationIds),
      status
    });
  }, [selectedConversationIds, bulkUpdateStatusMutation]);

  /* ---------------- Bubble Sidebar Handlers ---------------- */
  const handleToggleBubbleSidebar = useCallback(() => {
    setIsBubbleSidebarMinimized(prev => !prev);
  }, []);

  const handleOpenBubbleSettings = useCallback(() => {
    setDetailsOpen(true);
  }, []);

  const handleOpenBubbleOrganizers = useCallback(() => {
    // Implement organizers view or show coming soon
    openDialog('Info', 'Organizers view coming soon!');
  }, [openDialog]);

  /* ---------------- UI Handlers ---------------- */
  const toggleLeftPanel = useCallback(() => {
    setIsLeftPanelCollapsed((prev) => !prev);
  }, []);

  const handleSidebarResizeStart = useCallback((event) => {
    if (isMobile) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);

    const startX = event.clientX;
    const startWidth = conversationSidebarWidth;
    const originalCursor = document.body.style.cursor;
    const originalUserSelect = document.body.style.userSelect;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handlePointerMove = (moveEvent) => {
      const nextWidth = Math.min(
        CONVERSATION_SIDEBAR_MAX_WIDTH,
        Math.max(CONVERSATION_SIDEBAR_MIN_WIDTH, startWidth + moveEvent.clientX - startX)
      );
      setConversationSidebarWidth(nextWidth);
    };

    const handlePointerUp = () => {
      document.body.style.cursor = originalCursor;
      document.body.style.userSelect = originalUserSelect;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  }, [conversationSidebarWidth, isMobile]);

  const handleSidebarResizeKeyDown = useCallback((event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    setConversationSidebarWidth((width) => {
      const delta = event.key === 'ArrowRight' ? 24 : -24;
      return Math.min(
        CONVERSATION_SIDEBAR_MAX_WIDTH,
        Math.max(CONVERSATION_SIDEBAR_MIN_WIDTH, width + delta)
      );
    });
  }, []);

  const handleSelectConversation = useCallback((convoId) => {
    setSelectedConversationId(convoId);
    navigate(`?conversation=${convoId}`, { replace: true });
    if (isMobile) setIsLeftPanelCollapsed(true);
  }, [navigate, isMobile]);

  const handleBackToList = useCallback(() => {
    setSelectedConversationId(null);
    navigate('', { replace: true });
    if (isLeftPanelCollapsed) setIsLeftPanelCollapsed(false);
    refetchConversations();
  }, [navigate, isLeftPanelCollapsed, refetchConversations]);

  const handleRefresh = useCallback(() => {
    refetchConversations();
  }, [refetchConversations]);

  const handleOpenNewChat = useCallback(() => {
    setSelectedConversationId('new');
    navigate(`?conversation=new`, { replace: true });
    if (isMobile) setIsLeftPanelCollapsed(true);
  }, [navigate, isMobile]);

  const handleChatCreated = useCallback((convoId, fullResponse) => {
    console.log('[ConversationsHub] Chat created:', convoId);
    handleSelectConversation(convoId);
    queryClient.invalidateQueries(apiConfig.queryKey);
  }, [queryClient, apiConfig.queryKey, handleSelectConversation]);

  const handleCloseAddParticipant = useCallback(() => {
    setAddParticipantOpen(false);
    setInviteEmails('');
  }, []);

  const handleToggleParticipants = useCallback(() => setParticipantsOpen((prev) => !prev), []);
  const handleToggleDetails = useCallback(() => setDetailsOpen((prev) => !prev), []);
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  /* ---------------- Get conversation type indicators ---------------- */
  const getConversationTypeIcon = useCallback((convo) => {
    const isStarred = organizedConversations.starred?.some(c => c.id === convo.id);
  
    if (isStarred) {
      return (
        <Tooltip title="Starred">
          <Star
            fontSize="small"
            sx={{
              color: 'warning.main',
              opacity: 0.9
            }}
          />
        </Tooltip>
      );
    }
  
    if (isBubbleConversation(convo)) {
      return (
        <Tooltip title="Community Bubble Chat">
          <GroupsIcon 
            fontSize="medium" 
            sx={{ 
              color: 'success.main', 
              opacity: 0.85,
              filter: 'drop-shadow(0 0 2px rgba(76, 175, 80, 0.3))'
            }} 
          />
        </Tooltip>
      );
    } else if (isPartnershipConversation(convo)) {
      return (
        <Tooltip title="Partnership Chat">
          <PartnershipIcon
            fontSize="small"
            sx={{
              color: 'secondary.main',
              opacity: 0.7
            }}
          />
        </Tooltip>
      );
    } else if (isCampaignConversation(convo)) {
      return (
        <Tooltip title="Campaign Chat">
          <CampaignIcon
            fontSize="small"
            sx={{
              color: 'primary.main',
              opacity: 0.7
            }}
          />
        </Tooltip>
      );
    }
    return null;
  }, [isPartnershipConversation, isCampaignConversation, isBubbleConversation, organizedConversations.starred]);

  const getConversationTypeChip = useCallback((convo) => {
    if (isBubbleConversation(convo)) {
      return (
        <Chip
          size="small"
          label="Bubble"
          icon={<GroupsIcon fontSize="small" />}
          color="success"
          variant="filled"
          sx={{
            height: 22,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            boxShadow: '0 2px 4px rgba(76, 175, 80, 0.2)',
            '& .MuiChip-icon': {
              marginLeft: '4px',
              fontSize: '0.9rem'
            }
          }}
        />
      );
    } else if (isPartnershipConversation(convo)) {
      return (
        <Chip
          size="small"
          label="Partnership"
          color="secondary"
          variant="outlined"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600
          }}
        />
      );
    } else if (isCampaignConversation(convo)) {
      return (
        <Chip
          size="small"
          label="Campaign"
          color="primary"
          variant="outlined"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600
          }}
        />
      );
    }
    return null;
  }, [isPartnershipConversation, isCampaignConversation, isBubbleConversation]);

  /* ---------------- iOS Safari Viewport Fix ---------------- */
  useEffect(() => {
    if (isMobileSafari) {
      const setVH = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };
    
      setVH();
      window.addEventListener('resize', setVH);
      window.addEventListener('orientationchange', setVH);
    
      return () => {
        window.removeEventListener('resize', setVH);
        window.removeEventListener('orientationchange', setVH);
      };
    }
  }, [isMobileSafari]);

  /* ---------------- URL Sync & Lifecycle Effects ---------------- */
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const convoId = searchParams.get('conversation');
    if (convoId) {
      if (convoId === 'new') {
        setSelectedConversationId('new');
      } else {
        setSelectedConversationId(Number(convoId));
      }
    }
    if (isCreator) {
      const withId = searchParams.get('with');
      if (withId) {
        setNewChatUserId(Number(withId));
        setChatModalOpen(true);
      }
    }
  }, [location.search, isCreator]);

  useEffect(() => {
    const allConversations = [
      ...(organizedConversations.starred || []),
      ...(organizedConversations.active || []),
      ...(organizedConversations.archived || []),
      ...(organizedConversations.requested || [])
    ];
  
    if ((isCreator || isUser) && allConversations.length === 0 && !isLoadingConversations) {
      refetchConversations();
    }
  }, [isCreator, isUser, organizedConversations, isLoadingConversations, refetchConversations]);

  useEffect(() => {
    if (isMobile && selectedConversationId && selectedConversationId !== 'new') {
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;
      const originalTop = document.body.style.top;
      const scrollY = window.scrollY;

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.left = '0';
      document.body.style.right = '0';

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
        document.body.style.height = originalHeight;
        document.body.style.top = originalTop;
        document.body.style.left = '';
        document.body.style.right = '';
      
        if (scrollY) {
          window.scrollTo(0, scrollY);
        }
      };
    }
  }, [isMobile, selectedConversationId]);

  /* ---------------- Render Chat Screen ---------------- */
  const renderChatScreen = useCallback(() => {
    if (selectedConversationId === 'new') {
      return (
        <CreateChatScreen
          onClose={handleBackToList}
          onCreated={handleChatCreated}
        />
      );
    }

    if (!selectedConversationId) {
      return null;
    }

    if (isLoadingSelectedConversation) {
      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2
        }}>
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Loading conversation...
          </Typography>
        </Box>
      );
    }

    if (selectedConversationError) {
      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2,
          p: 3
        }}>
          <Typography variant="h6" color="error" gutterBottom>
            Failed to load conversation
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {selectedConversationError?.response?.data?.error || selectedConversationError.message}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => queryClient.invalidateQueries(['conversation', 'single', selectedConversationId])}
              startIcon={<Refresh />}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              onClick={handleBackToList}
              startIcon={<ArrowBack />}
            >
              Back to List
            </Button>
          </Box>
        </Box>
      );
    }

    const chatType = getSelectedConversationChatType();
    const campaignId = getCampaignId();
    const partnershipId = getPartnershipId();
    const bubbleId = getBubbleId();
    const creatorId = getCreatorId();

    const commonProps = {
      sx: {
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        position: 'relative',
        WebkitOverflowScrolling: 'touch',
      }
    };

    switch (chatType) {
      case 'bubble':
        return (
          <BubbleChatScreen
            conversationId={selectedConversationId}
            bubbleId={bubbleId}
            onNavigateBack={handleBackToList}
            {...commonProps}
          />
        );

      case 'partnership':
        return (
          <PartnershipChatScreen
            partnershipId={partnershipId}
            onNavigateBack={handleBackToList}
            {...commonProps}
          />
        );
    
      case 'campaign':
        return (
          <CampaignChatScreen
            conversationId={selectedConversationId}
            campaignId={campaignId}
            creatorId={creatorId}
            {...commonProps}
          />
        );
    
      default:
        return (
          <ChatScreen
            conversationId={selectedConversationId}
            conversationTheme={selectedConversation?.theme}
            {...commonProps}
          />
        );
    }
  }, [
    selectedConversationId,
    selectedConversation?.theme,
    isLoadingSelectedConversation,
    selectedConversationError,
    getSelectedConversationChatType,
    getCampaignId,
    getPartnershipId,
    getBubbleId,
    getCreatorId,
    handleBackToList,
    handleChatCreated,
    queryClient
  ]);

  /* ---------------- Render Context Menu ---------------- */
  const renderContextMenu = () => {
    if (!contextMenuConvo) return null;
  
    const isStarred = organizedConversations.starred?.some(c => c.id === contextMenuConvo.id);
    const isArchived = organizedConversations.archived?.some(c => c.id === contextMenuConvo.id);
    const isRequested = organizedConversations.requested?.some(c => c.id === contextMenuConvo.id);
  
    return (
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={handleCloseContextMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {isRequested && (
          <MenuItem onClick={() => {
            handleAcceptRequest(contextMenuConvo.id);
            handleCloseContextMenu();
          }}>
            <ChatBubbleOutline sx={{ mr: 1, color: 'success.main' }} />
            Accept Request
          </MenuItem>
        )}
      
        <MenuItem onClick={() => {
          handleStatusChange(contextMenuConvo.id, isStarred ? 'active' : 'starred');
        }}>
          {isStarred ? <StarBorder sx={{ mr: 1 }} /> : <Star sx={{ mr: 1 }} />}
          {isStarred ? 'Unstar' : 'Star'}
        </MenuItem>
      
        <MenuItem onClick={() => {
          handleStatusChange(contextMenuConvo.id, isArchived ? 'active' : 'archived');
        }}>
          {isArchived ? <Inbox sx={{ mr: 1 }} /> : <Archive sx={{ mr: 1 }} />}
          {isArchived ? 'Unarchive' : 'Archive'}
        </MenuItem>
      </Menu>
    );
  };

  /* ---------------- Render Conversation List with Tabs ---------------- */
  const renderConversationList = () => {
    const requestedCount = organizedConversations.requested?.length || 0;
    const allSelected = filteredConversations.length > 0 &&
      filteredConversations.every(c => selectedConversationIds.has(c.id));
  
    return (
      <>
        <HeaderContainer>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight="600">
              {isMultiSelectMode ? `${selectedConversationIds.size} selected` : 'Conversations'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isMultiSelectMode ? (
                <>
                  <Tooltip title="Cancel">
                    <IconButton onClick={toggleMultiSelectMode} size="small">
                      <Close />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={handleRefresh}
                      size="small"
                      disabled={isFetchingConversations}
                    >
                      <Refresh sx={{
                        animation: isFetchingConversations ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        }
                      }} />
                    </IconButton>
                  </Tooltip>
                  {!isMobile && (
                    <Tooltip title={isLeftPanelCollapsed ? "Expand" : "Collapse"}>
                      <IconButton onClick={toggleLeftPanel} size="small">
                        <ChevronRight sx={{
                          transform: isLeftPanelCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                          transition: 'transform 0.2s'
                        }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
          </Box>

          {isMultiSelectMode && selectedConversationIds.size > 0 && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Inbox />}
                onClick={() => handleBulkAction('active')}
                disabled={bulkUpdateStatusMutation.isLoading}
              >
                Move to Active
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Star />}
                onClick={() => handleBulkAction('starred')}
                disabled={bulkUpdateStatusMutation.isLoading}
              >
                Star
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Archive />}
                onClick={() => handleBulkAction('archived')}
                disabled={bulkUpdateStatusMutation.isLoading}
              >
                Archive
              </Button>
            </Box>
          )}

          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            <StyledTab
              label="Active"
              value="active"
              icon={<ChatBubbleOutline fontSize="small" />}
              iconPosition="start"
            />
          
            <StyledTab
              label="Archived"
              value="archived"
              icon={<Archive fontSize="small" />}
              iconPosition="start"
            />

            <StyledTab
              label={
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <span>Requested</span>
                  {requestedCount > 0 && (
                    <Box
                      component="span"
                      sx={{
                        minWidth: 16,
                        height: 16,
                        px: 0.6,
                        borderRadius: 999,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {requestedCount > 99 ? '99+' : requestedCount}
                    </Box>
                  )}
                </Box>
              }
              value="requested"
              icon={<Inbox fontSize="small" />}
              iconPosition="start"
            />
          </StyledTabs>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <StyledSearchField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{
                      color: isSearchFocused ? 'primary.main' : 'action.active',
                      transition: 'color 0.2s'
                    }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      sx={{ p: 0.5 }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {filteredConversations.length > 0 && (
              <Tooltip title={isMultiSelectMode ? "Exit multi-select" : "Select multiple"}>
                <IconButton
                  onClick={toggleMultiSelectMode}
                  color={isMultiSelectMode ? "primary" : "default"}
                  sx={{
                    border: isMultiSelectMode ? 2 : 1,
                    borderColor: isMultiSelectMode ? 'primary.main' : 'divider'
                  }}
                >
                  {isMultiSelectMode ? <CheckBox /> : <CheckBoxOutlineBlank />}
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {isMultiSelectMode && filteredConversations.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedConversationIds.size > 0 && !allSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      selectAllConversations();
                    } else {
                      deselectAllConversations();
                    }
                  }}
                />
              }
              label={allSelected ? "Deselect All" : "Select All"}
              sx={{ mb: 1 }}
            />
          )}

          {isMobile && !isMultiSelectMode && (
            <Button
              variant="contained"
              onClick={handleOpenNewChat}
              fullWidth
              size="large"
              startIcon={isCreator ? <AddComment /> : <Send />}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s',
              }}
            >
              {isCreator ? 'Start New Chat' : 'Create New Chat'}
            </Button>
          )}
        </HeaderContainer>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            background: "#ECECEC",
            p: 1,
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.primary, 0.2),
              borderRadius: 3,
            },
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
          }}
        >
          {isLoadingConversations ? (
            <Box sx={{ p: 1 ,background:"#ECECEC"}}>
              {[...Array(8)].map((_, i) => (
                <Card key={i} sx={{ mb: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Skeleton variant="circular" width={48} height={48} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="70%" height={24} />
                      <Skeleton variant="text" width="90%" height={20} />
                    </Box>
                    <Skeleton variant="circular" width={24} height={24} />
                  </Box>
                </Card>
              ))}
            </Box>
          ) : filteredConversations.length === 0 ? (
            <EmptyStateContainer sx={{ height: 'auto', py: 8 ,backaground:"#ECECEC"}}>
              {activeTab === 'requested' ? <Inbox sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} /> :
               activeTab === 'archived' ? <Archive sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} /> :
               <ChatBubbleOutline sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />}
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {searchQuery ? 'No conversations found' :
                 activeTab === 'requested' ? 'No pending requests' :
                 activeTab === 'archived' ? 'No archived conversations' :
                 'No active conversations'}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {searchQuery
                  ? 'Try adjusting your search terms'
                  : activeTab === 'active' ? 'Start a new conversation to get chatting!' : ''}
              </Typography>
              {isMobile && !searchQuery && activeTab === 'active' && (
                <Button
                  variant="outlined"
                  onClick={handleOpenNewChat}
                  sx={{ mt: 2 }}
                  startIcon={<AddComment />}
                >
                  Start Your First Chat
                </Button>
              )}
            </EmptyStateContainer>
          ) : (
            <List sx={{ p: 0, width: '100%', background: "#ECECEC" }}>
              {filteredConversations.map((convo, index) => {
                const isSelected = selectedConversationIds.has(convo.id);
                const isRequested = activeTab === 'requested';
              
                return (
                  <Zoom in timeout={200 + index * 50} key={convo.id}>
                    <div>
                      <ConversationCard
                        selected={convo.id === selectedConversationId && !isMultiSelectMode}
                        onClick={() => {
                          if (isMultiSelectMode) {
                            toggleConversationSelection(convo.id);
                          } else {
                            handleSelectConversation(convo.id);
                          }
                        }}
                        elevation={0}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {isMultiSelectMode && (
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleConversationSelection(convo.id);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              size="small"
                            />
                          )}
                          
                          <Avatar
                            src={getConvoAvatar(convo)}
                            sx={{
                              width: 44,
                              height: 44,
                            }}
                          >
                            {getConvoName(convo).charAt(0).toUpperCase()}
                          </Avatar>
                        
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight="600"
                                noWrap
                                sx={{
                                  color: convo.id === selectedConversationId && !isMultiSelectMode ? 'primary.main' : 'text.primary',
                                }}
                              >
                                {getConvoName(convo)}
                              </Typography>
                            
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {convo.unread_count > 0 ? (
                                  <Box
                                    sx={{
                                      bgcolor: 'primary.main',
                                      color: 'white',
                                      borderRadius: '10px',
                                      minWidth: 18,
                                      height: 18,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.65rem',
                                      fontWeight: 600,
                                      px: 0.5,
                                    }}
                                  >
                                    {convo.unread_count > 99 ? '99+' : convo.unread_count}
                                  </Box>
                                ) : null}
                               
                                {!isMultiSelectMode && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleOpenContextMenu(e, convo)}
                                    sx={{ 
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.2) },
                                    }}
                                    className="more-menu-btn"
                                  >
                                    <MoreVert fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                sx={{ 
                                  flex: 1, 
                                  fontSize: '0.8rem',
                                  color: convo.unread_count > 0 ? 'text.primary' : 'text.secondary',
                                  fontWeight: convo.unread_count > 0 ? 500 : 400,
                                }}
                              >
                                {getLastMessagePreview(convo)}
                              </Typography>
                              {getConversationTypeIcon(convo)}
                            </Box>
                          </Box>
                        </CardContent>
                      </ConversationCard>
                    </div>
                  </Zoom>
                );
              })}
            </List>
          )}
        </Box>
      </>
    );
  };

  /* ---------------- Loading & Error States ---------------- */
  if ((isCreator || isUser) && isLoadingConversations && !organizedConversations.active?.length) {
    return (
      <EmptyStateContainer>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Loading your conversations...
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Please wait while we fetch your chat history
        </Typography>
      </EmptyStateContainer>
    );
  }

  if (convError) {
    return (
      <EmptyStateContainer>
        <Typography variant="h6" color="error" gutterBottom>
          Oops! Something went wrong
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {convError.message}
        </Typography>
        <Button variant="contained" onClick={refetchConversations} startIcon={<Refresh />}>
          Try Again
        </Button>
      </EmptyStateContainer>
    );
  }

  /* ------------------------------------------------------------------ */
  /* MOBILE VIEW */
  /* ------------------------------------------------------------------ */
  if (isMobile && !selectedConversationId) {
    return (
      <Fade in timeout={300}>
        <Box
          sx={{
            height: `calc(100vh - ${BOTTOM_NAV_HEIGHT}px)`,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            overflow: 'hidden',
          }}
        >
          {renderConversationList()}
          {renderContextMenu()}
          {isCreator && (
            <ChatModalCreator
              open={chatModalOpen}
              onClose={() => setChatModalOpen(false)}
              recipientUserId={newChatUserId}
              sx={{ zIndex: getDialogZIndex() }}
            />
          )}
          <AlertDialog alertState={dialogState} />
        </Box>
      </Fade>
    );
  }

  if (isMobile && selectedConversationId) {
    const chatType = selectedConversation ? getChatScreenType(selectedConversation) : (selectedConversationId === 'new' ? 'new' : 'regular');
    
    // ✅ BUBBLE CHAT: Full-screen mode without traditional header
    if (chatType === 'bubble') {
      return (
        <Fade in timeout={300}>
          <Box
            sx={{
              height: `calc(100vh - ${BOTTOM_NAV_HEIGHT}px)`,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.default',
              position: 'relative',
              zIndex: Z_INDEX.MOBILE_CHAT_OVERLAY,
              ...(isIOS && {
                paddingLeft: 'env(safe-area-inset-left)',
                paddingRight: 'env(safe-area-inset-right)',
                paddingBottom: 'env(safe-area-inset-bottom)',
              }),
            }}
          >
            {renderChatScreen()}
            {isCreator && (
              <ChatModalCreator
                open={chatModalOpen}
                onClose={() => setChatModalOpen(false)}
                recipientUserId={newChatUserId}
                sx={{ zIndex: getDialogZIndex() }}
              />
            )}
            <AlertDialog alertState={dialogState} />
          </Box>
        </Fade>
      );
    }

    // Regular chat rendering with header
    return (
      <Fade in timeout={300}>
        <Box
          sx={{
            height: `calc(100vh - ${BOTTOM_NAV_HEIGHT}px)`,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            position: 'relative',
            zIndex: Z_INDEX.MOBILE_CHAT_OVERLAY,
            ...(isIOS && {
              paddingLeft: 'env(safe-area-inset-left)',
              paddingRight: 'env(safe-area-inset-right)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }),
          }}
        >
          {chatType === 'regular' && selectedConversationId !== 'new' && (
            <HeaderContainer sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: HEADER_HEIGHT,
              maxHeight: HEADER_HEIGHT,
              py: 1.5,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <Tooltip title="Back to conversations">
                  <IconButton onClick={handleBackToList} sx={{ mr: 1 }}>
                    <ArrowBack />
                  </IconButton>
                </Tooltip>
                {selectedConversation && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    <Avatar
                      src={getConvoAvatar(selectedConversation)}
                      sx={{ width: 40, height: 40 }}
                    >
                      {getConvoName(selectedConversation).charAt(0).toUpperCase()}
                    </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" fontWeight="600" noWrap>
                          {selectedConversation.display_name || getConvoName(selectedConversation)}
                        </Typography>
                        {getConversationTypeChip(selectedConversation)}
                      </Box>
                      {(() => {
                        const roomOnline = selectedConversationId ? getRoomOnlineUsers(selectedConversationId) : { online_count: 0, online_users: [] };
                        const onlineCount = roomOnline?.online_count || 0;
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {onlineCount > 0 ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <FiberManualRecordIcon sx={{ fontSize: 8, color: '#4CAF50' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {onlineCount} {onlineCount === 1 ? 'online' : 'online'}
                                </Typography>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <FiberManualRecordIcon sx={{ fontSize: 8, color: 'text.disabled' }} />
                                <Typography variant="caption" color="text.disabled">
                                  offline
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                )}
                {selectedConversationId && selectedConversationId !== 'new' && hasParticipantAccess && (
                  <Tooltip title="Edit conversation details">
                    <IconButton onClick={() => setUpdateDetailsDialogOpen(true)} size="small">
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Manage participants">
                  <IconButton onClick={handleToggleParticipants}>
                    <PersonAdd />
                  </IconButton>
                </Tooltip>
                <Tooltip title="More options">
                  <IconButton onClick={handleToggleDetails}>
                    <MoreVert />
                  </IconButton>
                </Tooltip>
              </Box>
            </HeaderContainer>
          )}

          <Box sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            height: chatType === 'regular' && selectedConversationId !== 'new'
              ? `calc(100% - ${HEADER_HEIGHT}px)`
              : '100%',
          }}>
            <Fade in timeout={300}>
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                position: 'relative',
                height: '100%',
              }}>
                {renderChatScreen()}
              </Box>
            </Fade>
          </Box>

          {chatType === 'regular' && selectedConversationId !== 'new' && hasParticipantAccess && (
            <>
              <Dialog
                open={participantsOpen}
                onClose={handleToggleParticipants}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                sx={{ zIndex: getDialogZIndex() }}
                PaperProps={{
                  sx: {
                    borderRadius: { xs: 0, sm: 3 },
                    height: { xs: '85vh', sm: '80vh' },
                    maxHeight: { xs: '85vh', sm: '80vh' },
                  }
                }}
              >
                <ParticipantsPanel
                  conversationId={selectedConversationId}
                  onClose={handleToggleParticipants}
                />
              </Dialog>

              <Dialog
                open={detailsOpen}
                onClose={handleToggleDetails}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                sx={{ zIndex: getDialogZIndex() }}
                PaperProps={{
                  sx: {
                    borderRadius: { xs: 0, sm: 3 },
                    height: { xs: '85vh', sm: '80vh' },
                    maxHeight: { xs: '85vh', sm: '80vh' },
                  }
                }}
              >
                <ConversationDetailsPanel
                  conversationId={selectedConversationId}
                  onClose={handleToggleDetails}
                />
              </Dialog>

              <UpdateConversationDetailsDialog
                open={updateDetailsDialogOpen}
                onClose={() => setUpdateDetailsDialogOpen(false)}
                conversation={selectedConversation}
                sx={{ zIndex: getDialogZIndex() }}
              />
            </>
          )}

          {isCreator && (
            <ChatModalCreator
              open={chatModalOpen}
              onClose={() => setChatModalOpen(false)}
              recipientUserId={newChatUserId}
              sx={{ zIndex: getDialogZIndex() }}
            />
          )}
          <AlertDialog alertState={dialogState} />
        </Box>
      </Fade>
    );
  }

  /* ------------------------------------------------------------------ */
  /* DESKTOP VIEW - WITH BUBBLE CHAT SPECIAL HANDLING */
  /* ------------------------------------------------------------------ */
  
  // ✅ KEY CHANGE: Detect if current conversation is a bubble chat
  const isBubbleChat = selectedConversation && getChatScreenType(selectedConversation) === 'bubble';
  const showBubbleSidebar = isBubbleChat && selectedConversationId;
  const showConversationSidebar = !showBubbleSidebar && !isLeftPanelCollapsed;
  
  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: 'flex',
          height: `calc(100vh - 1px)`,
          width: '100%',
          overflow: 'hidden',
          bgcolor: 'background.default',
          position: 'relative',
        }}
      >
        {/* ✅ BUBBLE SIDEBAR - Shows when bubble chat is selected */}
        {showBubbleSidebar && (
          <BubbleInfoSidebar
            bubbleId={getBubbleId()}  // ✅ FIXED: Pass bubbleId instead of bubble object
            organizers={[]} // You can fetch organizers separately if needed
            canManage={selectedConversation?.is_host || selectedConversation?.is_admin || false}
            isPolling={!document.hidden && !!selectedConversationId}
            onNavigateBack={handleBackToList}
            onOpenSettings={handleOpenBubbleSettings}
            onOpenOrganizers={handleOpenBubbleOrganizers}
            onOpenParticipants={handleToggleParticipants}
            minimized={isBubbleSidebarMinimized}
            onToggleMinimized={handleToggleBubbleSidebar}
          />
        )}

        {/* ✅ Conversation List Sidebar - Hidden when bubble chat is active */}
        {showConversationSidebar && (
          <Box
            sx={{
              width: conversationSidebarWidth,
              minWidth: conversationSidebarWidth,
              height: '100%',
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
            }}
          >
            {renderConversationList()}
            <Box
              role="separator"
              aria-label="Resize conversations sidebar"
              aria-orientation="vertical"
              tabIndex={0}
              onPointerDown={handleSidebarResizeStart}
              onKeyDown={handleSidebarResizeKeyDown}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 10,
                height: '100%',
                cursor: 'col-resize',
                zIndex: 20,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 2,
                  height: '100%',
                  bgcolor: 'transparent',
                  transition: 'background-color 0.15s',
                },
                '&:hover::after, &:focus-visible::after': {
                  bgcolor: 'primary.main',
                },
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            borderLeft: selectedConversationId ? '1px solid' : 'none',
            borderColor: 'divider',
            position: 'relative',
          }}
        >
          {/* ✅ BUBBLE CHAT: No header, full control in BubbleChatScreen */}
          {isBubbleChat ? (
            <Box sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              height: '100%',
            }}>
              {renderChatScreen()}
            </Box>
          ) : (
            <>
              {/* Regular chat header - ONLY shown for non-bubble chats */}
              {selectedConversation && getChatScreenType(selectedConversation) === 'regular' && selectedConversationId !== 'new' && (
                <HeaderContainer sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: HEADER_HEIGHT,
                  maxHeight: HEADER_HEIGHT,
                  py: 1,
                  px: 1.5,
                  
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    <Tooltip title={isLeftPanelCollapsed ? "Show sidebar" : "Hide sidebar"}>
                      <IconButton onClick={toggleLeftPanel} sx={{ mr: 1 }}>
                        {isLeftPanelCollapsed ? <ChevronRight /> : <ChevronLeft />}
                      </IconButton>
                    </Tooltip>
                    {selectedConversation && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                        <Avatar
                          src={getConvoAvatar(selectedConversation)}
                          sx={{ width: 40, height: 40 }}
                        >
                          {getConvoName(selectedConversation).charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" fontWeight="600" noWrap>
                              {selectedConversation.display_name || getConvoName(selectedConversation)}
                            </Typography>
                            {getConversationTypeChip(selectedConversation)}
                          </Box>
                          {(() => {
                            const roomOnline = selectedConversationId ? getRoomOnlineUsers(selectedConversationId) : { online_count: 0, online_users: [] };
                            const participantCount = selectedConversation.participants?.length || 0;
                            const onlineCount = roomOnline?.online_count || 0;
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {participantCount} {participantCount === 1 ? 'participant' : 'participants'}
                                </Typography>
                                {participantCount > 0 && (
                                  <Typography variant="caption" color="text.disabled">•</Typography>
                                )}
                                {onlineCount > 0 ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    <FiberManualRecordIcon sx={{ fontSize: 8, color: '#4CAF50' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {onlineCount} {onlineCount === 1 ? 'online' : 'online'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    <FiberManualRecordIcon sx={{ fontSize: 8, color: 'text.disabled' }} />
                                    <Typography variant="caption" color="text.disabled">
                                      offline
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}
                        </Box>
                      </Box>
                    )}
                    {selectedConversationId && selectedConversationId !== 'new' && hasParticipantAccess && (
                      <Tooltip title="Edit conversation details">
                        <IconButton onClick={() => setUpdateDetailsDialogOpen(true)} size="small">
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {selectedConversationId && selectedConversationId !== 'new' && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Manage participants">
                        <IconButton onClick={handleToggleParticipants}>
                          <PersonAdd />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More options">
                        <IconButton onClick={handleToggleDetails}>
                          <MoreVert />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </HeaderContainer>
              )}
              
              {/* Regular chat area */}
              <Box sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                height: selectedConversation && getChatScreenType(selectedConversation) === 'regular' && selectedConversationId !== 'new'
                  ? `calc(100% - ${HEADER_HEIGHT}px)`
                  : '100%',
                maxHeight: selectedConversation && getChatScreenType(selectedConversation) === 'regular' && selectedConversationId !== 'new'
                  ? `calc(100% - ${HEADER_HEIGHT}px)`
                  : '100%',
              }}>
                {selectedConversationId ? (
                  <Fade in timeout={300}>
                    <Box sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      position: 'relative',
                      height: '100%',
                    }}>
                      {renderChatScreen()}
                    </Box>
                  </Fade>
                ) : (
                  <EmptyStateContainer>
                    <ChatBubbleOutline sx={{ fontSize: 80, color: 'text.disabled', mb: 3 }} />
                    <Typography variant="h5" color="text.secondary" gutterBottom fontWeight="500">
                      Welcome to Chat
                    </Typography>
                    <Typography variant="body1" color="text.disabled" paragraph>
                      Select a conversation from the sidebar to start chatting,
                      or create a new conversation to get started.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleOpenNewChat}
                      startIcon={<AddComment />}
                      size="large"
                      sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                      }}
                    >
                      Start New Conversation
                    </Button>
                  </EmptyStateContainer>
                )}
              </Box>
            </>
          )}
        </Box>

        {renderContextMenu()}

        {/* ✅ Only show these dialogs for non-bubble regular chats */}
        {!isBubbleChat && (!selectedConversation || (getChatScreenType(selectedConversation) === 'regular' && selectedConversationId !== 'new')) && hasParticipantAccess && (
          <>
            <Dialog
              open={participantsOpen}
              onClose={handleToggleParticipants}
              maxWidth="sm"
              fullWidth
              sx={{ zIndex: getDialogZIndex() }}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  height: '80vh',
                  maxHeight: '80vh',
                }
              }}
            >
              <ParticipantsPanel
                conversationId={selectedConversationId}
                onClose={handleToggleParticipants}
              />
            </Dialog>

            <Dialog
              open={detailsOpen}
              onClose={handleToggleDetails}
              maxWidth="sm"
              fullWidth
              sx={{ zIndex: getDialogZIndex() }}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  height: '80vh',
                  maxHeight: '80vh',
                }
              }}
            >
              <ConversationDetailsPanel
                conversationId={selectedConversationId}
                onClose={handleToggleDetails}
              />
            </Dialog>

            <UpdateConversationDetailsDialog
              open={updateDetailsDialogOpen}
              onClose={() => setUpdateDetailsDialogOpen(false)}
              conversation={selectedConversation}
              sx={{ zIndex: getDialogZIndex() }}
            />
          </>
        )}

        {isCreator && (
          <ChatModalCreator
            open={chatModalOpen}
            onClose={() => setChatModalOpen(false)}
            recipientUserId={newChatUserId}
            sx={{ zIndex: getDialogZIndex() }}
          />
        )}
        <AlertDialog alertState={dialogState} />
      </Box>
    </Fade>
  );
}
